// "Backoffice Breakout" — the third discovery layer (Easter egg → God Mode → hidden game). A full-
// viewport canvas overlay plays Breakout OVER the real page: actual cards/buttons/menu items become
// the bricks, and hitting one visually hides the real element. Nothing is ever clicked or submitted —
// the overlay swallows all pointer events — and ESC restores every hidden element. Self-cleaning, like
// matrixRain.ts. No leaderboard; only a personal best + silly local stats live in localStorage.
import { notifications } from '@mantine/notifications';

const KEY = 'god_mode_arcade';
const LABELS = ['LEGACY', 'BUG', 'TODO', 'MEETING', 'URGENT', 'SPAM', 'DUPLICATE', 'MONDAY', 'BACKLOG'];
// Real elements that make satisfying bricks.
const BRICK_SELECTOR =
    'button, a[href], [class*="Card-root"], [class*="NavLink-root"], [class*="ActionIcon-root"], [class*="Paper-root"]';

interface ArcadeState {
    unlocked: boolean;
    best: number;
}

function readArcade(): ArcadeState {
    try {
        const raw = localStorage.getItem(KEY);
        if (raw) {
            const s = JSON.parse(raw) as Partial<ArcadeState>;
            return { unlocked: !!s.unlocked, best: s.best ?? 0 };
        }
    } catch {
        /* ignore */
    }
    return { unlocked: false, best: 0 };
}

function writeArcade(patch: Partial<ArcadeState>): void {
    try {
        localStorage.setItem(KEY, JSON.stringify({ ...readArcade(), ...patch }));
    } catch {
        /* ignore */
    }
}

export const isArcadeUnlocked = (): boolean => readArcade().unlocked;
export const unlockArcade = (): void => writeArcade({ unlocked: true });

interface Brick {
    el: HTMLElement | null;
    left: number;
    top: number;
    right: number;
    bottom: number;
    destroyed: boolean;
    label: string;
    hue: number;
    originalVisibility: string;
}

export function startBreakout(): void {
    if (typeof document === 'undefined') return;

    const HUD_H = 52;
    const ballR = 10;
    const paddleW = 120;
    const paddleH = 14;

    let W = window.innerWidth;
    let H = window.innerHeight;

    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;z-index:99999;cursor:default;';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        canvas.remove();
        return;
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const dpr = window.devicePixelRatio || 1;
    const sizeCanvas = () => {
        W = window.innerWidth;
        H = window.innerHeight;
        canvas.width = Math.floor(W * dpr);
        canvas.height = Math.floor(H * dpr);
        canvas.style.width = `${W}px`;
        canvas.style.height = `${H}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    sizeCanvas();

    // Collect real elements as bricks (snapshot their boxes; skip the HUD strip and paddle zone).
    const collectBricks = (): Brick[] => {
        const nodes = Array.from(document.querySelectorAll<HTMLElement>(BRICK_SELECTOR));
        const out: Brick[] = [];
        for (const el of nodes) {
            const r = el.getBoundingClientRect();
            if (r.width < 24 || r.height < 12 || r.width > 340) continue;
            if (r.bottom < HUD_H + 8 || r.top > H - 90) continue;
            if (r.right < 0 || r.left > W) continue;
            out.push({
                el,
                left: r.left,
                top: r.top,
                right: r.right,
                bottom: r.bottom,
                destroyed: false,
                label: LABELS[Math.floor(Math.random() * LABELS.length)],
                hue: (out.length * 37) % 360,
                originalVisibility: el.style.visibility,
            });
            if (out.length >= 70) break;
        }
        return out;
    };

    // Fallback synthetic grid if the page has too few bricks.
    const syntheticBricks = (): Brick[] => {
        const out: Brick[] = [];
        const cols = 8;
        const bw = Math.min(120, (W - 80) / cols);
        const bh = 26;
        const gap = 8;
        const totalW = cols * bw + (cols - 1) * gap;
        const startX = (W - totalW) / 2;
        for (let row = 0; row < 5; row++) {
            for (let col = 0; col < cols; col++) {
                const left = startX + col * (bw + gap);
                const top = HUD_H + 30 + row * (bh + gap);
                out.push({
                    el: null,
                    left,
                    top,
                    right: left + bw,
                    bottom: top + bh,
                    destroyed: false,
                    label: LABELS[Math.floor(Math.random() * LABELS.length)],
                    hue: (out.length * 37) % 360,
                    originalVisibility: '',
                });
            }
        }
        return out;
    };

    let bricks = collectBricks();
    if (bricks.length < 8) bricks = [...bricks, ...syntheticBricks()];

    let paddleX = W / 2;
    const paddleY = () => H - 40;
    let bx = paddleX;
    let by = paddleY() - 24;
    const speed = 6;
    let vx = speed * 0.55;
    let vy = -speed;
    let lives = 3;
    let destroyed = 0;
    let over = false;
    let won = false;
    const startedAt = Date.now();

    const resetBall = () => {
        bx = paddleX;
        by = paddleY() - 24;
        vx = speed * 0.55 * (Math.random() < 0.5 ? 1 : -1);
        vy = -speed;
    };

    let leftKey = false;
    let rightKey = false;

    const onMouseMove = (e: MouseEvent) => {
        paddleX = Math.max(paddleW / 2, Math.min(W - paddleW / 2, e.clientX));
    };
    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            exit();
            return;
        }
        if (e.key === 'ArrowLeft') leftKey = true;
        if (e.key === 'ArrowRight') rightKey = true;
    };
    const onKeyUp = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') leftKey = false;
        if (e.key === 'ArrowRight') rightKey = false;
    };
    const onResize = () => {
        sizeCanvas();
        // Re-snapshot real bricks (their boxes still resolve even while hidden).
        for (const b of bricks) {
            if (!b.el) continue;
            const r = b.el.getBoundingClientRect();
            b.left = r.left;
            b.top = r.top;
            b.right = r.right;
            b.bottom = r.bottom;
        }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('resize', onResize);

    let raf = 0;

    const update = () => {
        if (over) return;

        if (leftKey) paddleX = Math.max(paddleW / 2, paddleX - 9);
        if (rightKey) paddleX = Math.min(W - paddleW / 2, paddleX + 9);

        bx += vx;
        by += vy;

        // Walls.
        if (bx < ballR) { bx = ballR; vx = -vx; }
        if (bx > W - ballR) { bx = W - ballR; vx = -vx; }
        if (by < HUD_H + ballR) { by = HUD_H + ballR; vy = -vy; }

        // Paddle.
        const py = paddleY();
        if (vy > 0 && by + ballR >= py && by + ballR <= py + paddleH + 14 && bx >= paddleX - paddleW / 2 && bx <= paddleX + paddleW / 2) {
            vy = -Math.abs(vy);
            vx += ((bx - paddleX) / (paddleW / 2)) * 2.4;
            vx = Math.max(-9, Math.min(9, vx));
        }

        // Bricks (first hit per frame).
        for (const b of bricks) {
            if (b.destroyed) continue;
            if (bx + ballR > b.left && bx - ballR < b.right && by + ballR > b.top && by - ballR < b.bottom) {
                const minX = Math.min(bx + ballR - b.left, b.right - (bx - ballR));
                const minY = Math.min(by + ballR - b.top, b.bottom - (by - ballR));
                if (minX < minY) vx = -vx;
                else vy = -vy;
                b.destroyed = true;
                destroyed += 1;
                if (b.el) b.el.style.visibility = 'hidden';
                break;
            }
        }

        // Lost ball.
        if (by - ballR > H) {
            lives -= 1;
            if (lives <= 0) endGame(false);
            else resetBall();
        }

        if (bricks.every((b) => b.destroyed)) endGame(true);
    };

    const draw = () => {
        ctx.clearRect(0, 0, W, H);

        // On game over, dim the whole screen and show only the panel (no paddle/ball behind it).
        if (over) {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.78)';
            ctx.fillRect(0, 0, W, H);
            drawGameOver();
            return;
        }

        // Bricks — translucent tint over the real element so it shows through, then vanishes when hit.
        for (const b of bricks) {
            if (b.destroyed) continue;
            const w = b.right - b.left;
            const h = b.bottom - b.top;
            ctx.fillStyle = `hsla(${b.hue}, 80%, 55%, 0.30)`;
            ctx.fillRect(b.left, b.top, w, h);
            ctx.strokeStyle = `hsla(${b.hue}, 85%, 60%, 0.9)`;
            ctx.lineWidth = 2;
            ctx.strokeRect(b.left + 1, b.top + 1, w - 2, h - 2);
            if (w > 44 && h > 16) {
                const cx = b.left + w / 2;
                const cy = b.top + h / 2;
                ctx.font = 'bold 12px system-ui, sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineWidth = 3;
                ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
                ctx.strokeText(b.label, cx, cy, w - 6);
                ctx.fillStyle = '#fff';
                ctx.fillText(b.label, cx, cy, w - 6);
            }
        }

        // Paddle.
        const py = paddleY();
        ctx.fillStyle = '#feda4a';
        ctx.fillRect(paddleX - paddleW / 2, py, paddleW, paddleH);

        // Ball — red core, white ring and a glow so it stays visible on any background.
        ctx.save();
        ctx.beginPath();
        ctx.arc(bx, by, ballR, 0, Math.PI * 2);
        ctx.shadowColor = 'rgba(255, 60, 60, 0.9)';
        ctx.shadowBlur = 16;
        ctx.fillStyle = '#ff3b3b';
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
        ctx.restore();

        // HUD.
        ctx.fillStyle = 'rgba(0,0,0,0.62)';
        ctx.fillRect(0, 0, W, HUD_H);
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#feda4a';
        ctx.font = 'bold 15px system-ui, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('🎮 BACKOFFICE BREAKOUT — Clear the board.', 16, HUD_H / 2);
        ctx.fillStyle = '#fff';
        ctx.font = '13px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`Bricks destroyed: ${destroyed}    ${'♥'.repeat(Math.max(0, lives))}`, W / 2, HUD_H / 2);
        ctx.textAlign = 'right';
        ctx.fillText('ESC — Return to work', W - 16, HUD_H / 2);
    };

    const drawGameOver = () => {
        const bw = 360;
        const bh = 210;
        const x = (W - bw) / 2;
        const y = (H - bh) / 2;
        ctx.fillStyle = 'rgba(0,0,0,0.88)';
        ctx.fillRect(x, y, bw, bh);
        ctx.strokeStyle = '#feda4a';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, bw, bh);

        const secs = Math.round((Date.now() - startedAt) / 1000);
        const mmss = `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`;
        const best = readArcade().best;

        ctx.textAlign = 'center';
        ctx.fillStyle = '#feda4a';
        ctx.font = 'bold 22px system-ui, sans-serif';
        ctx.fillText(won ? '🏆 PRODUCTION SECURED' : '💥 GAME OVER', W / 2, y + 40);

        ctx.fillStyle = '#fff';
        ctx.font = '14px system-ui, sans-serif';
        const lines = [
            `Bricks destroyed: ${destroyed}`,
            `Productivity sacrificed: ${mmss}`,
            `Personal best: ${best}`,
        ];
        lines.forEach((l, i) => ctx.fillText(l, W / 2, y + 78 + i * 24));

        ctx.fillStyle = '#feda4a';
        ctx.font = 'bold 13px system-ui, sans-serif';
        ctx.fillText('ESC — Return to work', W / 2, y + bh - 20);
    };

    const endGame = (didWin: boolean) => {
        over = true;
        won = didWin;
        if (destroyed > readArcade().best) writeArcade({ best: destroyed });
    };

    // Fixed-step accumulator: run physics at a constant 60 Hz no matter the display refresh rate, so
    // the ball/paddle move at the same real-world speed on 120 Hz and 30 Hz screens alike. Draw once
    // per animation frame. (Fixed steps also keep collisions reliable — no delta-scaled tunnelling.)
    const STEP_MS = 1000 / 60;
    let lastTime = 0;
    let acc = 0;
    const loop = (now: number) => {
        raf = requestAnimationFrame(loop);
        if (!lastTime) lastTime = now;
        let delta = now - lastTime;
        lastTime = now;
        if (delta > 250) delta = 250; // clamp after a tab-away so we don't fast-forward a huge burst
        acc += delta;
        while (acc >= STEP_MS) {
            update();
            acc -= STEP_MS;
        }
        draw();
    };
    raf = requestAnimationFrame(loop);

    function exit(): void {
        cancelAnimationFrame(raf);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        window.removeEventListener('resize', onResize);
        document.body.style.overflow = prevOverflow;
        // Restore every hidden element.
        for (const b of bricks) {
            if (b.el) b.el.style.visibility = b.originalVisibility;
        }
        canvas.remove();
        notifications.show({
            title: '💼 PROFESSIONAL MODE RESTORED',
            message: 'Nothing happened. We will never speak of this again.',
            color: 'grape',
        });
    }
}
